import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'offline-sync-queue';

const OfflineSyncContext = createContext(null);

const isBrowser = typeof window !== 'undefined';

const loadInitialQueue = () => {
  if (!isBrowser) return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to parse offline sync queue:', error);
    return [];
  }
};

const buildEntry = (type, payload, metadata = {}) => ({
  id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  type,
  payload,
  metadata,
  createdAt: new Date().toISOString()
});

export const OfflineSyncProvider = ({ children }) => {
  const [queue, setQueue] = useState(loadInitialQueue);
  const [isOnline, setIsOnline] = useState(isBrowser ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const queueRef = useRef(queue);
  const processingRef = useRef(false);

  useEffect(() => {
    queueRef.current = queue;
    if (isBrowser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    }
  }, [queue]);

  const syncPainLog = useCallback(async (payload) => {
    const formData = new FormData();

    const appendField = (key, value) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'string' && !value.trim()) return;
      if (Array.isArray(value)) {
        if (value.length === 0) return;
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    };

    formData.append('zoneId', payload.zoneId);
    formData.append('zoneName', payload.zoneName);
    formData.append('painLevel', payload.painLevel);
    formData.append('painColor', payload.painColor);
    formData.append('view', payload.view);

    appendField('painIntensity', payload.painIntensity);
    appendField('notes', payload.notes);
    appendField('symptoms', payload.symptoms);
    appendField('triggers', payload.triggers);
    appendField('timeOfDay', payload.timeOfDay);
    appendField('mood', payload.mood);
    appendField('activity', payload.activity);
    appendField('duration', payload.duration);
    appendField('medication', payload.medication);
    appendField('distressScore', payload.distressScore);
    appendField('distressNotes', payload.distressNotes);
    appendField('distressSources', payload.distressSources);

    await axios.post('/api/pain-logs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }, []);

  const triggerSync = useCallback(async () => {
    if (!isBrowser || processingRef.current) {
      return;
    }

    if (!navigator.onLine) {
      setIsOnline(false);
      return;
    }

    if (!queueRef.current.length) {
      setLastSync(new Date().toISOString());
      return;
    }

    processingRef.current = true;
    setSyncing(true);

    const currentQueue = [...queueRef.current];
    const nextQueue = [];
    let syncedCount = 0;
    let abortRemaining = false;

    for (const entry of currentQueue) {
      if (abortRemaining) {
        nextQueue.push(entry);
        continue;
      }

      try {
        if (entry.type === 'painLog') {
          await syncPainLog(entry.payload);
        } else if (entry.type === 'distressLog') {
          await axios.post('/api/distress-logs', entry.payload);
        } else {
          console.warn('Unknown offline queue entry type:', entry.type);
        }
        syncedCount += 1;
      } catch (error) {
        const isNetworkError = !error.response;
        const status = error.response?.status;
        const isServerError = status >= 500;

        if (isNetworkError || isServerError) {
          nextQueue.push(entry);
          abortRemaining = true;
          if (isNetworkError) {
            setIsOnline(false);
          }
        } else {
          console.error('Removing invalid offline entry after failed sync:', error);
          toast.error(
            entry.type === 'painLog'
              ? 'Unable to sync a pain log entry (validation error).'
              : 'Unable to sync a distress entry (validation error).'
          );
        }
      }
    }

    // Silently sync without notification

    setQueue(nextQueue);
    setLastSync(new Date().toISOString());
    setSyncing(false);
    processingRef.current = false;

    if (!nextQueue.length) {
      setIsOnline(navigator.onLine);
    }
  }, [syncPainLog]);

  useEffect(() => {
    if (!isBrowser) return;

    const handleOnline = () => {
      setIsOnline(true);
      triggerSync();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast('Offline mode enabled. New entries will sync automatically when online.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerSync]);

  useEffect(() => {
    if (isOnline && queue.length && !processingRef.current) {
      triggerSync();
    }
  }, [isOnline, queue.length, triggerSync]);

  const queuePainLog = useCallback((payload, metadata = {}) => {
    const entry = buildEntry('painLog', payload, metadata);
    setQueue(prev => [...prev, entry]);
    toast.success('Pain log saved offline. We will sync it when you are online.');
    return entry;
  }, []);

  const queueDistressLog = useCallback((payload) => {
    const entry = buildEntry('distressLog', payload, { score: payload.distressScore });
    setQueue(prev => [...prev, entry]);
    toast.success('Distress entry saved offline. We will sync it automatically.');
    return entry;
  }, []);

  const pendingPainLogs = useMemo(
    () =>
      queue
        .filter(entry => entry.type === 'painLog')
        .map(entry => ({
          ...entry.payload,
          localId: entry.id,
          createdAt: entry.createdAt
        })),
    [queue]
  );

  const pendingDistressLogs = useMemo(
    () =>
      queue
        .filter(entry => entry.type === 'distressLog')
        .map(entry => ({
          ...entry.payload,
          localId: entry.id,
          createdAt: entry.createdAt
        })),
    [queue]
  );

  const value = useMemo(
    () => ({
      isOnline,
      syncing,
      lastSync,
      queueLength: queue.length,
      pendingPainLogs,
      pendingDistressLogs,
      queuePainLog,
      queueDistressLog,
      triggerSync
    }),
    [
      isOnline,
      lastSync,
      pendingDistressLogs,
      pendingPainLogs,
      queue.length,
      queueDistressLog,
      queuePainLog,
      syncing,
      triggerSync
    ]
  );

  return (
    <OfflineSyncContext.Provider value={value}>
      {children}
    </OfflineSyncContext.Provider>
  );
};

export const useOfflineSync = () => {
  const context = useContext(OfflineSyncContext);
  if (!context) {
    throw new Error('useOfflineSync must be used within an OfflineSyncProvider');
  }
  return context;
};


